import { useEffect, useState } from 'react';
import type { Guides } from '../lib/configs.ts';
import { LIFT_NAME } from '../lib/defaults.ts';
import { useRepo } from '../lib/hooks.ts';
import { Button } from './ui.tsx';

/** The guides stored on this device (no network: the session must never wait, J24 / basement rule). */
export function useGuides(): Guides | null {
  const { store } = useRepo();
  const [g, setG] = useState<Guides | null>(null);
  useEffect(() => {
    let alive = true;
    void store.meta<{ hash: string; data: Guides }>('config:guides').then((m) => alive && setG(m?.data ?? null));
    return () => {
      alive = false;
    };
  }, [store]);
  return g;
}

/** J24 (EG01): how to do an exercise, as a sheet over the session, which keeps running underneath. */
export function GuideSheet({ exercise, guides, onClose }: { exercise: string; guides: Guides | null; onClose: () => void }) {
  const g = guides?.guides[exercise];
  const name = LIFT_NAME[exercise];
  const [tab, setTab] = useState<'steps' | 'faults'>('steps');
  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end" role="dialog" aria-modal="true" aria-label={`How to do ${name}`} onClick={onClose}>
      <div className="w-full max-w-[480px] mx-auto max-h-[85vh] overflow-y-auto bg-surface-raised rounded-t-[20px] p-5 space-y-3" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
        <h2 className="text-title">{name}</h2>
        {!g ? (
          <p className="text-text-muted">{guides ? "There's no guide for this exercise yet." : 'Guides download the first time this device is online.'}</p>
        ) : (
          <>
            {/* EG01 final (Wave 2.4 batch 2/3): two tabs; numbered steps; each fault as the problem, then the fix */}
            <div className="flex gap-1 bg-surface-sunken rounded-[14px] p-1" role="tablist" aria-label="Guide">
              {(['steps', 'faults'] as const).map((t) => (
                <button key={t} type="button" role="tab" aria-selected={tab === t} disabled={t === 'faults' && !g.faults.length} className={`flex-1 min-h-11 rounded-[10px] text-sm ${tab === t ? 'bg-surface-raised text-text font-semibold' : 'text-text-muted'}`} onClick={() => setTab(t)}>
                  {t === 'steps' ? 'Steps' : 'Common faults'} <span className="num text-xs text-text-muted">{t === 'steps' ? g.steps.length : g.faults.length}</span>
                </button>
              ))}
            </div>
            {tab === 'steps' ? (
              <ol className="space-y-2" aria-label="Steps">
                {g.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 bg-surface-sunken rounded-[14px] p-3">
                    <span className="num w-8 h-8 rounded-full bg-surface-raised grid place-items-center shrink-0 font-bold" aria-hidden="true">{i + 1}</span>
                    <span className="pt-1">{s}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <ul className="space-y-2" aria-label="Common faults">
                {g.faults.map((s, i) => {
                  const cut = s.indexOf(': ');
                  const what = cut > 0 ? s.slice(0, cut) : s;
                  const fix = cut > 0 ? s.slice(cut + 2) : '';
                  return (
                    <li key={i} className="bg-surface-sunken rounded-[14px] p-3">
                      <span className="block text-headline">
                        <span className="text-miss" aria-hidden="true">✕ </span>
                        {what}
                      </span>
                      {fix && <span className="block mt-1">{fix[0]!.toUpperCase() + fix.slice(1)}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
        <Button onClick={onClose}>Back to the set</Button>
        <p className="text-xs text-text-muted text-center">Your workout and rest timer keep running.</p>
      </div>
    </div>
  );
}
